import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Tabs, Tab } from 'react-bootstrap';
import { FaArrowLeft, FaEdit, FaSave, FaExternalLinkAlt, FaPlayCircle } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import Spinner from '../components/Spinner';
import * as api from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParentAgents } from '../context/ParentAgentContext'; // Updated import

const AgentSpecific = () => {
  const { parentId, id } = useParams();
  const navigate = useNavigate();
  const { getParentAgentForAgent } = useParentAgents(); // Use the context
  
  const [agent, setAgent] = useState(null);
  const [microAgent, setMicroAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({});
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);


  useEffect(() => {
    const fetchAgentData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch parent agent details
        const agentResponse = await api.getAgentById(parentId);
        if (agentResponse && agentResponse.data) {
          setAgent(agentResponse.data);
        } else {
          throw new Error('Failed to load parent agent details');
        }
        
        // Fetch micro agent details
        try {
          // Fetch from the micro agent endpoint
          const microAgentResponse = await api.getMicroAgents(parentId);
          if (microAgentResponse.data) {
            const microAgentData = microAgentResponse.data.find(ma => ma.id === id);
            if (microAgentData) {
              setMicroAgent(microAgentData);
            } else {
              throw new Error('Micro agent not found');
            }
          }
        } catch (err) {
          console.error('Error fetching micro agent:', err);
          setError('Failed to load micro agent details. Please try again later.');
        }
        
        // Fetch schema for this micro agent
        try {
          const schemaResponse = await api.getSchema(parentId, 'micro-agent', id);
          if (schemaResponse) {
            setSchema(schemaResponse);
            
            // Initialize form data with default values
            if (schemaResponse.properties) {
              const initialData = {};
              Object.keys(schemaResponse.properties).forEach(key => {
                initialData[key] = schemaResponse.properties[key].default || '';
              });
              setFormData(initialData);
            }
          }
        } catch (err) {
          console.error('Error fetching schema:', err);
          // Non-critical error, don't set main error state
          toast.error('Failed to load agent schema');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching agent data:', err);
        setError(err.message || 'Failed to load agent data. Please try again later.');
        setLoading(false);
      }
    };

    fetchAgentData();
  }, [parentId, id]);

  const handleInputChange = (name, value, type) => {
    let processedValue = value;

    // Handle different input types
    if (type === 'number') {
      processedValue = value === '' ? '' : Number(value);
    } else if (type === 'boolean') {
      processedValue = Boolean(value);
    } else if (type === 'array' && typeof value === 'string') {
      // Handle arrays from string inputs (comma or newline separated)
      processedValue = value.split(/[,\n]/).map(item => item.trim()).filter(Boolean);
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleExecute = async () => {
    try {
      setExecuting(true);
      setExecutionResult(null);
      setError(null);
      
      const response = await api.testAgent(parentId, 'micro-agent', id, formData);
      
      if (response) {
        setExecutionResult(response);
        toast.success('Execution completed successfully');
      }
    } catch (err) {
      console.error('Error executing agent:', err);
      setError('Failed to execute agent. Please try again later.');
      toast.error('Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const handleGoToTest = () => {
    navigate(`/agents/${parentId}/micro-agents/${id}/testing`);
  };

  const handleGoToLogs = () => {
    navigate(`/agents/${parentId}/micro-agents/${id}/logs`);
  };

  const handleGoBack = () => {
    navigate(`/parent/${parentId}`);
  };

  // Render a form field based on its schema
  const renderField = (name, schema) => {
    const value = formData[name] !== undefined ? formData[name] : '';
    
    switch (schema.type) {
      case 'boolean':
        return (
          <Form.Check
            type="checkbox"
            label={schema.title || name}
            checked={Boolean(value)}
            onChange={(e) => handleInputChange(name, e.target.checked, 'boolean')}
          />
        );
        
      case 'number':
        return (
          <Form.Control
            type="number"
            value={value}
            onChange={(e) => handleInputChange(name, e.target.value, 'number')}
            placeholder={`Enter ${schema.title || name}`}
          />
        );
        
      case 'array':
        return (
          <Form.Control
            as="textarea"
            rows={4}
            value={Array.isArray(value) ? value.join('\n') : value}
            onChange={(e) => handleInputChange(name, e.target.value, 'array')}
            placeholder={`Enter ${schema.title || name} (one per line)`}
          />
        );
        
      case 'object':
        return (
          <Form.Control
            as="textarea"
            rows={6}
            value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleInputChange(name, parsed, 'object');
              } catch (err) {
                // Let them type an invalid JSON temporarily
                handleInputChange(name, e.target.value, 'string');
              }
            }}
            placeholder={`Enter ${schema.title || name} (JSON format)`}
          />
        );
        
      default:
        if (schema.format === 'textarea' || name.toLowerCase().includes('prompt')) {
          return (
            <Form.Control
              as="textarea"
              rows={6}
              value={value}
              onChange={(e) => handleInputChange(name, e.target.value, 'string')}
              placeholder={`Enter ${schema.title || name}`}
            />
          );
        }
        
        if (schema.enum) {
          return (
            <Form.Select
              value={value}
              onChange={(e) => handleInputChange(name, e.target.value, 'string')}
            >
              <option value="">Select {schema.title || name}</option>
              {schema.enum.map((option, index) => (
                <option key={index} value={option}>
                  {schema.options && schema.options[index] ? schema.options[index] : option}
                </option>
              ))}
            </Form.Select>
          );
        }
        
        return (
          <Form.Control
            type="text"
            value={value}
            onChange={(e) => handleInputChange(name, e.target.value, 'string')}
            placeholder={`Enter ${schema.title || name}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <Container className="py-5 d-flex justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
        <div className="text-center">
          <Spinner />
          <p className="mt-3">Loading agent details...</p>
        </div>
      </Container>
    );
  }

  if (error || !agent || !microAgent) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Agent</Alert.Heading>
          <p>{error || 'Agent not found'}</p>
          <Button variant="outline-primary" onClick={() => navigate(`/parent/${parentId}`)}>
            Back to Parent Agent
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4 main-container">
      <ToastContainer position="top-right" autoClose={3000} />
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1>Micro Agent: {microAgent.name}</h1>
              <p className="text-muted">{microAgent.description}</p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={handleGoBack}>
                <FaArrowLeft className="me-2" />
                Back to Parent Agent
              </Button>
              <Button variant="outline-primary" onClick={handleGoToTest}>
                <FaPlayCircle className="me-2" />
                Test Agent
              </Button>
              <Button variant="outline-info" onClick={handleGoToLogs}>
                <FaExternalLinkAlt className="me-2" />
                View Logs
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="overview" title="Overview">
          <Card>
            <Card.Body>
              <h4 className="mb-3">Micro Agent Details</h4>
              <Row>
                <Col md={6}>
                  <p><strong>ID:</strong> {microAgent.id}</p>
                  <p><strong>Name:</strong> {microAgent.name}</p>
                  <p><strong>Description:</strong> {microAgent.description}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Parent Agent:</strong> {agent.name}</p>
                  <p><strong>Status:</strong> {microAgent.status || 'Active'}</p>
                  <p><strong>Development Status:</strong> {microAgent.devStatus || 'Not Deployed'}</p>
                  <p><strong>Production Status:</strong> {microAgent.prodStatus || 'Not Deployed'}</p>
                </Col>
              </Row>
              
              {microAgent.capabilities && microAgent.capabilities.length > 0 && (
                <div className="mt-4">
                  <h5>Capabilities</h5>
                  <ul>
                    {microAgent.capabilities.map((capability, index) => (
                      <li key={index}>{capability}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="quickTest" title="Quick Test">
          <Card>
            <Card.Body>
              <h4 className="mb-3">Test Micro Agent</h4>
              {schema?.properties ? (
                <Form>
                  <Row>
                    {Object.entries(schema.properties).map(([name, propSchema]) => (
                      <Col md={6} key={name} className="mb-3">
                        <Form.Group>
                          <Form.Label>{propSchema.title || name}</Form.Label>
                          {renderField(name, propSchema)}
                          {propSchema.description && (
                            <Form.Text className="text-muted">
                              {propSchema.description}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    ))}
                  </Row>
                  
                  <div className="d-grid gap-2 mt-4">
                    <Button
                      variant="primary"
                      onClick={handleExecute}
                      disabled={executing}
                    >
                      {executing ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Executing...
                        </>
                      ) : (
                        'Execute Test'
                      )}
                    </Button>
                  </div>
                  
                  {executionResult && (
                    <div className="mt-4">
                      <h5>Execution Result</h5>
                      <pre className="p-3 bg-light rounded">
                        {typeof executionResult === 'object' 
                          ? JSON.stringify(executionResult, null, 2) 
                          : executionResult}
                      </pre>
                    </div>
                  )}
                </Form>
              ) : (
                <Alert variant="info">
                  No schema available for this micro agent. Please visit the Testing page for a more complete testing experience.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="development" title="Development">
          <Card>
            <Card.Body>
              <h4 className="mb-3">Development Options</h4>
              <Row>
                <Col md={4} className="mb-4">
                  <Card>
                    <Card.Body>
                      <h5>Edit Environment Variables</h5>
                      <p>Configure the environment variables for this micro agent.</p>
                      <Button 
                        variant="outline-primary" 
                        onClick={() => navigate(`/agents/${parentId}/micro-agents/${id}`)}
                      >
                        Edit Environment Variables
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={4} className="mb-4">
                  <Card>
                    <Card.Body>
                      <h5>Edit Prompts</h5>
                      <p>Manage and edit the prompts used by this micro agent.</p>
                      <Button 
                        variant="outline-primary" 
                        onClick={() => navigate(`/agents/${parentId}/micro-agents/${id}`)}
                      >
                        Edit Prompts
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={4} className="mb-4">
                  <Card>
                    <Card.Body>
                      <h5>Testing</h5>
                      <p>Run comprehensive tests on this micro agent with various inputs.</p>
                      <Button 
                        variant="outline-primary" 
                        onClick={handleGoToTest}
                      >
                        Go to Testing
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="logs" title="Recent Logs">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">Recent Executions</h4>
                <Button 
                  variant="primary" 
                  onClick={handleGoToLogs}
                >
                  View All Logs
                </Button>
              </div>
              
              <Alert variant="info">
                Please navigate to the Logs page to view execution history and detailed logs.
              </Alert>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default AgentSpecific;